import React, { useMemo } from "react";
import { Typography, Box, Card, CardContent } from "@mui/material";
import { InfoBox } from "./StandardLayout";
import { colors } from "../theme";
import { Expense } from "../utils/interface";
import { computeCostBreakdown, parseNumericField } from "../utils/projectCostCalcs";

const formatCurrencySafe = (value: any) => {
  if (value === undefined || value === null || value === "" || value === "N/A") return "—";
  const num = Number(String(value).replace(/[^0-9.-]/g, ""));
  if (!Number.isFinite(num)) return String(value);
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(num);
};

const MetricRow = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ py: 1, borderBottom: `1px solid ${colors.grey[300]}` }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block", mb: 0.25, fontSize: "0.65rem" }}
    >
      {label}
    </Typography>
    <Typography className="num" variant="body2" sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
  </Box>
);

/** Display a dollar value or "TBD" when the category has no data yet. */
const displayCostOrTBD = (value: number): string => {
  if (!value || value <= 0) return "TBD";
  return formatCurrencySafe(value);
};

interface ConstructionLoanSummaryProps {
  exactLoanAmount: number | string;
  modelDetails: any;
  expenses: Expense[];
  variables: any;
}

export default function ConstructionLoanSummary({
  exactLoanAmount,
  modelDetails,
  expenses,
  variables,
}: ConstructionLoanSummaryProps) {
  const loanAmount = useMemo(() => parseNumericField(exactLoanAmount), [exactLoanAmount]);

  // Interest Reserve must be part of Total Project Costs so the two LTC %s in the
  // top and bottom summary cards reconcile (previously the bottom LTC came out
  // ~2–3pp high because the denominator was missing the reserve). Source of truth:
  // the same `Sr. Cons. Estimated Interest Reserve` variable the top Construction
  // Loan Summary card displays, parsed as a number.
  const interestReserve = useMemo(
    () => parseNumericField(variables?.["Sr. Cons. Estimated Interest Reserve"]),
    [variables],
  );

  // All cost math lives in utils/projectCostCalcs so this card, the Second Lien
  // summary and the Hard Costs tab cannot disagree. It also resolves gross
  // buildable SF safely: the Development model type has two fields sharing the
  // key "Gross Buildable Square Feet", and picking the empty one used to zero out
  // every "$ / buildable sf" row (CEO report, Aug 2026).
  const {
    acquisitionPrice,
    hardCosts,
    closingCosts,
    softCosts,
    legalPreDevCosts,
    totalProjectCosts,
  } = useMemo(
    () =>
      computeCostBreakdown({
        expenses,
        modelDetails,
        constructionLoanAmount: loanAmount,
        interestReserve,
      }),
    [expenses, modelDetails, loanAmount, interestReserve],
  );

  return (
    <Card
      elevation={0}
      sx={{
        backgroundColor: colors.white,
        border: `1px solid ${colors.grey[300]}`,
        borderRadius: 2,
        mt: 2,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
          LTC Breakdown
        </Typography>

        {/* Primary metric: Total Project Costs */}
        <Box sx={{ mb: 3 }}>
          <InfoBox
            label="Total Project Costs"
            value={totalProjectCosts > 0 ? formatCurrencySafe(totalProjectCosts) : "—"}
            variant="default"
          />
        </Box>

        {/* Cost component rows */}
        <MetricRow label="Purchase Price of Land" value={displayCostOrTBD(acquisitionPrice)} />
        <MetricRow label="Construction Costs" value={displayCostOrTBD(hardCosts)} />
        <MetricRow label="Closing Costs" value={displayCostOrTBD(closingCosts)} />
        <MetricRow label="Soft Costs" value={displayCostOrTBD(softCosts)} />
        <MetricRow label="Legal / Pre-Development Costs" value={displayCostOrTBD(legalPreDevCosts)} />
        <MetricRow label="Interest Reserve" value={displayCostOrTBD(interestReserve)} />
      </CardContent>
    </Card>
  );
}
